import { useId, useState } from 'react';
import type { RefObject } from 'react';
import {
  AlertTriangleIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsiblePanel,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Label, RequiredMark } from '@/components/ui/label';
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useWorkspaceSecrets } from '@/features/secrets';
import type {
  ConnectionProviderView,
  ConnectionView,
} from '@/lib/api/generated/model';
import { formatRelativeTime } from '@/shared/utils/time';

import { isConnectionError } from '../errors';
import { useConnectProvider } from '../hooks/useConnectProvider';
import { useConnectionHealth } from '../hooks/useConnectionHealth';
import { useDeleteConnection } from '../hooks/useDeleteConnection';
import { useUpdateConnection } from '../hooks/useUpdateConnection';
import { DeleteConnectionDialog } from './DeleteConnectionDialog';
import { EnvTag } from './EnvTag';

function seedFields(
  provider: ConnectionProviderView,
  connection: ConnectionView | undefined,
): Record<string, string> {
  const seeded: Record<string, string> = {};
  for (const field of provider.fields) {
    if (field.kind === 'secret') continue;
    const stored = connection?.config[field.name];
    seeded[field.name] =
      typeof stored === 'boolean'
        ? String(stored)
        : typeof stored === 'string'
          ? stored
          : (field.default ?? '');
  }
  return seeded;
}

/**
 * Connect a catalog provider, or manage the one already connected — the one place
 * a GitHub/GitLab credential is configured (connections rework). Shared by the
 * Connections page and the Secrets page, so it owns its own mutations and its
 * delete confirmation rather than leaving each page to wire them twice.
 *
 * Everything provider-specific comes from the catalog: which fields exist, which
 * sit under *Advanced*, the "create token" link, the entries agents will see. The
 * token is never seeded — in edit mode it shows when it was set, and is only
 * re-typed after **Replace**. A blank token on save means "keep" server-side.
 *
 * Saving does not wait on a live test: the save invalidates the connection's
 * health query, and a rejected token shows as a warning on the card instead of
 * blocking this form (spec "Connect sheet"). `replaceToken` is that warning's way
 * on: it opens the sheet with the token input already open, and the reason the
 * last check gave shown at the top, so the fix does not start with a hunt.
 */
export function ConnectSheet({
  workspaceId,
  provider,
  connection,
  open,
  onClose,
  onSaved,
  replaceToken = false,
  finalFocus,
  deleteBlockedReason,
}: {
  workspaceId: string;
  provider: ConnectionProviderView;
  connection?: ConnectionView;
  open: boolean;
  onClose: () => void;
  onSaved?: (connection: ConnectionView) => void;
  /** Open with the token input showing, and the last check's failure above it. */
  replaceToken?: boolean;
  /** Where focus returns when the sheet closes, for a sheet opened from another form. */
  finalFocus?: RefObject<HTMLElement | null>;
  /** Why this connection cannot be removed right now (a registry an execution
   *  image still pulls from); shown instead of the confirmation. */
  deleteBlockedReason?: string;
}) {
  const editing = connection !== undefined;
  const [fields, setFields] = useState(() => seedFields(provider, connection));
  // A provider a workspace may hold many of (the registry) is told apart by its
  // name, so the name is asked for up front; for the rest it stays under Advanced.
  const multi = provider.multi_instance === true;
  const [name, setName] = useState(
    connection?.name ?? (multi ? '' : provider.display_name),
  );
  const [description, setDescription] = useState(connection?.description ?? '');
  const [enabled, setEnabled] = useState(connection?.enabled ?? true);
  const [token, setToken] = useState('');
  const [replacingToken, setReplacingToken] = useState(
    !editing || replaceToken,
  );
  const [submitted, setSubmitted] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const connect = useConnectProvider(workspaceId);
  const update = useUpdateConnection(workspaceId);
  const remove = useDeleteConnection(workspaceId);
  const { entries } = useWorkspaceSecrets(workspaceId);
  const health = useConnectionHealth(workspaceId, connection?.id);
  const healthFailure =
    replaceToken && health.data?.ok === false ? health.data.message : null;

  const isPending = connect.isPending || update.isPending;
  const error = editing ? update.error : connect.error;
  const serverMessage = error
    ? isConnectionError(error)
      ? error.message
      : 'The request failed. Please try again.'
    : undefined;

  // A field that depends on a toggle shows, and counts, only while it is on.
  const shown = provider.fields.filter(
    (field) => !field.depends_on || fields[field.depends_on] === 'true',
  );
  const tokenField = shown.find((field) => field.kind === 'secret');
  // Catalog order decides whether the token comes before or after the other
  // basic fields (GitHub: token first; Jira: site and email first).
  const tokenIndex = shown.findIndex((field) => field.kind === 'secret');
  const basicFields = shown.filter(
    (field) => field.kind !== 'secret' && !field.advanced,
  );
  const fieldsBeforeToken = basicFields.filter(
    (field) => tokenIndex === -1 || shown.indexOf(field) < tokenIndex,
  );
  const fieldsAfterToken = basicFields.filter(
    (field) => tokenIndex !== -1 && shown.indexOf(field) > tokenIndex,
  );
  const advancedFields = shown.filter(
    (field) => field.kind !== 'secret' && field.advanced,
  );
  const tokenEntry = entries?.find((entry) => entry.key === provider.token_env);
  // A saved credential needs no re-typing on edit, but a credential newly
  // required by the toggle (it was off when saved) must be typed.
  const hasSavedToken =
    editing && typeof connection.config.token_secret === 'string';
  // Only a credential that is on the form can be missing: a registry with
  // authentication off shows none.
  const tokenMissing =
    tokenField !== undefined &&
    (!editing || !hasSavedToken) &&
    token.trim() === '';
  const missingFields = shown.filter(
    (field) =>
      field.kind !== 'secret' &&
      field.kind !== 'toggle' &&
      field.required &&
      (fields[field.name] ?? '').trim() === '',
  );
  const nameMissing = multi && name.trim() === '';

  const formId = useId();
  const tokenId = useId();
  const nameId = useId();
  const descriptionId = useId();
  const enabledId = useId();

  function clearServerError() {
    if (editing) update.reset();
    else connect.reset();
  }

  function buildConfig(): Record<string, string | boolean> {
    const config: Record<string, string | boolean> = {};
    for (const field of shown) {
      if (field.kind === 'secret') continue;
      // A toggle goes over the wire as a boolean, not the 'true' it is held as.
      config[field.name] =
        field.kind === 'toggle'
          ? fields[field.name] === 'true'
          : (fields[field.name] ?? '');
    }
    // A toggle that is off takes its dependants' values with it, so the service
    // never sees a credential for an unauthenticated registry.
    if (tokenField && token.trim() !== '') config[tokenField.name] = token;
    return config;
  }

  // Edit sends `config` only when something in it changed: a blank credential
  // beside an unchanged address and toggle would otherwise read as "no credential".
  function configChanged(): boolean {
    if (!connection) return true;
    if (token.trim() !== '') return true;
    return provider.fields.some((field) => {
      if (field.kind === 'secret') return false;
      const stored = connection.config[field.name];
      const was =
        typeof stored === 'boolean'
          ? String(stored)
          : typeof stored === 'string'
            ? stored
            : (field.default ?? '');
      return (fields[field.name] ?? '') !== was;
    });
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSubmitted(true);
    if (tokenMissing || missingFields.length > 0 || nameMissing) return;

    const done = (saved: ConnectionView) => {
      toast.success(
        multi
          ? `${editing ? 'Updated' : 'Added'} ${saved.name}.`
          : editing
            ? `Updated ${provider.display_name}.`
            : `Connected ${provider.display_name}.`,
      );
      onSaved?.(saved);
      onClose();
    };

    if (connection) {
      update.mutate(
        {
          connectionId: connection.id,
          data: {
            name: name.trim() || provider.display_name,
            ...(multi
              ? { description: description.trim() || null, enabled }
              : {}),
            ...(configChanged() ? { config: buildConfig() } : {}),
          },
        },
        { onSuccess: done },
      );
      return;
    }
    connect.mutate(
      {
        type: provider.type,
        name: name.trim() || provider.display_name,
        ...(multi ? { description: description.trim() || null } : {}),
        enabled: multi ? enabled : true,
        config: buildConfig(),
      },
      { onSuccess: done },
    );
  }

  function handleDelete() {
    if (!connection) return;
    remove.mutate(connection.id, {
      onSuccess: () => {
        setConfirmingDelete(false);
        toast.success(`Disconnected ${provider.display_name}.`);
        onClose();
      },
      onError: () => {
        setConfirmingDelete(false);
        toast.error(`${provider.display_name} could not be disconnected.`);
      },
    });
  }

  function renderField(field: ConnectionProviderView['fields'][number]) {
    const id = `${formId}-${field.name}`;
    if (field.kind === 'toggle') {
      return (
        <div key={field.name} className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <input
              id={id}
              type="checkbox"
              className="accent-primary"
              disabled={isPending}
              checked={fields[field.name] === 'true'}
              onChange={(event) => {
                setFields({
                  ...fields,
                  [field.name]: String(event.target.checked),
                });
                clearServerError();
              }}
            />
            <Label htmlFor={id} className="font-normal">
              {field.label}
            </Label>
          </div>
          {field.help ? (
            <p className="text-muted-foreground text-xs">{field.help}</p>
          ) : null}
        </div>
      );
    }
    return (
      <div key={field.name} className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-0.5">
            <Label htmlFor={id}>{field.label}</Label>
            {field.required ? <RequiredMark /> : null}
          </div>
          {field.env ? <EnvTag name={field.env} id={`${id}-env`} /> : null}
        </div>
        <Input
          id={id}
          aria-describedby={field.env ? `${id}-env` : undefined}
          value={fields[field.name] ?? ''}
          onChange={(event) => {
            setFields({ ...fields, [field.name]: event.target.value });
            clearServerError();
          }}
          disabled={isPending}
          autoFocus={!editing && field === fieldsBeforeToken[0]}
          placeholder={field.default ?? undefined}
          className={field.kind === 'url' ? 'font-mono' : undefined}
          aria-invalid={
            submitted && missingFields.includes(field) ? true : undefined
          }
        />
        {submitted && missingFields.includes(field) ? (
          <p className="text-destructive text-xs">{field.label} is required.</p>
        ) : null}
        {field.help ? (
          <p className="text-muted-foreground text-xs">{field.help}</p>
        ) : null}
      </div>
    );
  }

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={(next) => {
          if (isPending) return;
          if (!next) onClose();
        }}
      >
        <SheetContent className="sm:max-w-lg" finalFocus={finalFocus}>
          <SheetHeader>
            <SheetTitle>
              {multi
                ? editing
                  ? `Edit ${connection.name}`
                  : `Add ${provider.display_name}`
                : editing
                  ? `Manage ${provider.display_name}`
                  : `Connect ${provider.display_name}`}
            </SheetTitle>
            <SheetDescription>{provider.blurb}</SheetDescription>
          </SheetHeader>

          <form
            onSubmit={handleSubmit}
            className="flex min-h-0 flex-1 flex-col"
          >
            <SheetBody className="flex flex-col gap-4">
              {healthFailure ? (
                <p
                  role="status"
                  className="text-warning border-warning/30 bg-warning/5 flex items-start gap-2 rounded-md border px-3 py-2 text-sm"
                >
                  <AlertTriangleIcon
                    aria-hidden="true"
                    className="mt-0.5 size-4 shrink-0"
                  />
                  {healthFailure}
                </p>
              ) : null}
              {multi ? (
                <>
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-0.5">
                      <Label htmlFor={nameId}>Name</Label>
                      <RequiredMark />
                    </div>
                    <Input
                      id={nameId}
                      autoFocus={!editing}
                      value={name}
                      onChange={(event) => {
                        setName(event.target.value);
                        clearServerError();
                      }}
                      disabled={isPending}
                      aria-invalid={submitted && nameMissing ? true : undefined}
                    />
                    {submitted && nameMissing ? (
                      <p className="text-destructive text-xs">
                        Name is required.
                      </p>
                    ) : null}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-1">
                      <Label htmlFor={descriptionId}>Description</Label>
                      <span className="text-muted-foreground text-xs">
                        (optional)
                      </span>
                    </div>
                    <Input
                      id={descriptionId}
                      value={description}
                      onChange={(event) => {
                        setDescription(event.target.value);
                      }}
                      disabled={isPending}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      id={enabledId}
                      type="checkbox"
                      className="accent-primary"
                      disabled={isPending}
                      checked={enabled}
                      onChange={(event) => {
                        setEnabled(event.target.checked);
                      }}
                    />
                    <Label htmlFor={enabledId} className="font-normal">
                      Enabled
                    </Label>
                  </div>
                </>
              ) : null}

              {fieldsBeforeToken.map(renderField)}

              {tokenField ? (
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-0.5">
                      <Label htmlFor={tokenId}>{tokenField.label}</Label>
                      {!editing ? <RequiredMark /> : null}
                    </div>
                    {tokenField.env ? (
                      <EnvTag name={tokenField.env} id={`${tokenId}-env`} />
                    ) : null}
                  </div>
                  {replacingToken ? (
                    <Input
                      id={tokenId}
                      type="password"
                      aria-describedby={
                        tokenField.env ? `${tokenId}-env` : undefined
                      }
                      autoComplete="off"
                      autoFocus={editing || fieldsBeforeToken.length === 0}
                      value={token}
                      onChange={(event) => {
                        setToken(event.target.value);
                        clearServerError();
                      }}
                      disabled={isPending}
                      aria-invalid={
                        submitted && tokenMissing ? true : undefined
                      }
                    />
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground font-mono text-sm">
                        ••••{' '}
                        {tokenEntry
                          ? `set ${formatRelativeTime(tokenEntry.updated_at)}`
                          : 'set'}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setReplacingToken(true);
                        }}
                      >
                        Replace
                      </Button>
                    </div>
                  )}
                  <p className="text-muted-foreground text-xs">
                    {provider.token_scopes.length > 0
                      ? `Needs ${provider.token_scopes.join(', ')}. `
                      : null}
                    {provider.token_url ? (
                      <a
                        href={provider.token_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary inline-flex items-center gap-1 underline-offset-4 hover:underline"
                      >
                        Create token
                        <ExternalLinkIcon
                          aria-hidden="true"
                          className="size-3"
                        />
                      </a>
                    ) : null}
                    {tokenField.help ? tokenField.help : null}
                    {editing && provider.token_url
                      ? ' · Steps already running keep the token they started with.'
                      : null}
                  </p>
                  {submitted && tokenMissing ? (
                    <p className="text-destructive text-xs">Paste a token.</p>
                  ) : null}
                </div>
              ) : null}

              {fieldsAfterToken.map(renderField)}

              {!multi || advancedFields.length > 0 ? (
                <Collapsible>
                  <CollapsibleTrigger className="text-muted-foreground hover:text-foreground group flex items-center gap-1 text-sm">
                    <ChevronRightIcon
                      aria-hidden="true"
                      className="size-4 transition-transform group-data-[panel-open]:rotate-90"
                    />
                    Advanced
                  </CollapsibleTrigger>
                  <CollapsiblePanel className="mt-3 flex flex-col gap-4">
                    {advancedFields.map(renderField)}
                    {!multi ? (
                      <div className="flex flex-col gap-1.5">
                        <Label htmlFor={nameId}>Display name</Label>
                        <Input
                          id={nameId}
                          value={name}
                          onChange={(event) => {
                            setName(event.target.value);
                          }}
                          disabled={isPending}
                        />
                      </div>
                    ) : null}
                  </CollapsiblePanel>
                </Collapsible>
              ) : null}

              {provider.exposed_env.length > 0 ? (
                <p className="text-muted-foreground border-border border-t pt-3 text-xs">
                  Each value is set as an environment variable in every agent
                  sandbox.
                </p>
              ) : null}

              {serverMessage ? (
                <p role="alert" className="text-destructive text-sm">
                  {serverMessage}
                </p>
              ) : null}
            </SheetBody>

            <SheetFooter>
              {editing ? (
                <Button
                  type="button"
                  variant="destructive"
                  className="mr-auto"
                  disabled={isPending}
                  onClick={() => {
                    if (deleteBlockedReason) toast.error(deleteBlockedReason);
                    else setConfirmingDelete(true);
                  }}
                >
                  {multi ? 'Delete' : 'Disconnect'}
                </Button>
              ) : null}
              <SheetClose
                render={<Button type="button" variant="outline" />}
                disabled={isPending}
              >
                Cancel
              </SheetClose>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? 'Saving…'
                  : editing
                    ? 'Save'
                    : multi
                      ? 'Add'
                      : 'Connect'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {connection && confirmingDelete ? (
        <DeleteConnectionDialog
          connectionName={connection.name}
          open
          isPending={remove.isPending}
          exposedEnv={connection.exposed_env}
          triggerProfileCount={connection.trigger_profile_count}
          verb={multi ? 'delete' : 'disconnect'}
          onConfirm={handleDelete}
          onCancel={() => {
            setConfirmingDelete(false);
          }}
        />
      ) : null}
    </>
  );
}
